import {
  isWeb,
  Button as TamaguiButton,
  ButtonProps as TamaguiButtonProps,
  TamaguiElement,
} from '@mythical/ui'
import Spinner from 'app/components/primitive/Spinner'
import React, { forwardRef, memo, useCallback } from 'react'
import { Linking } from 'react-native'
import { useLink } from 'solito/link'

export type ButtonProps = TamaguiButtonProps & {
  icon?: React.ReactElement
  loading?: boolean
  uppercase?: boolean
}

const ButtonComponent = forwardRef<TamaguiElement, ButtonProps>(
  (props, ref) => {
    const { loading, uppercase, textProps, transparent, ...rest } = props
    const isDisabled = loading || rest.disabled

    const buttonProps: ButtonProps = {
      br: '$pill',
      borderColor: '$borderColor', // Tamagui, why this is not the default?
      ...rest,
      disabled: isDisabled,
      disabledStyle: {
        opacity: 0.375,
        pointerEvents: 'none',
      },
      ...(loading && {
        icon: <Spinner size="small" />,
      }),
    }

    const _textProps: ButtonProps['textProps'] = {
      textTransform: uppercase ? 'uppercase' : 'none',

      // "Obviously" font line height is fucked on web. This might not work well for different font sizes
      ...(isWeb && { pos: 'relative', top: '-2.5%' }),
      ...textProps,
    }

    return <TamaguiButton ref={ref} {...buttonProps} textProps={_textProps} />
  },
)

ButtonComponent.displayName = 'Button'

export const Button = memo(ButtonComponent) as typeof ButtonComponent

export type ButtonLinkProps = {
  href: string
  onPressCustom?: () => void
  target?: string
} & ButtonProps

export const ButtonLink = forwardRef<TamaguiElement, ButtonLinkProps>(
  ({ href, onPressCustom, target, ...props }, ref) => {
    const linkProps = useLink({
      href,
    })

    const onPress = useCallback(
      (e: any) => {
        e.preventDefault()
        onPressCustom && onPressCustom()

        if (!href.startsWith('http') && !href.startsWith('/api')) {
          return linkProps.onPress()
        }

        if (isWeb) {
          // For API routes that redirect, use window.location.href instead of window.open
          // when target is _self or undefined (same window)
          if (href.startsWith('/api') && (!target || target === '_self')) {
            return (window.location.href = href)
          }
          return window.open(href, target)
        }

        Linking.openURL(href)
      },
      [href, onPressCustom, target, linkProps.onPress],
    )

    return <Button ref={ref} {...props} {...linkProps} onPress={onPress} />
  },
)

ButtonLink.displayName = 'ButtonLink'
